import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Building, Mail, Phone, Globe, MapPin, Save } from 'lucide-react';
import LoaderOverlay from '../common/LoaderOverlay';

export interface CompanySettings {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  website: string;
}

interface CompanyInformationProps {
  company: CompanySettings;
  isSaving: boolean;
  onInputChange: (field: string, value: string) => void;
  onSave: () => void;
}

const CompanyInformation = ({
  company,
  isSaving,
  onInputChange,
  onSave,
}: CompanyInformationProps) => {
  return (
    <LoaderOverlay isLoading={isSaving} loadingText="Saving...">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
            <Building className="h-5 w-5 flex-shrink-0" />
            <span>Company Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={company.name}
                onChange={e => onInputChange('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyEmail">Company Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="companyEmail"
                  type="email"
                  className="pl-10"
                  value={company.email}
                  onChange={e => onInputChange('email', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyPhone">Company Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="companyPhone"
                  className="pl-10"
                  value={company.phone}
                  onChange={e => onInputChange('phone', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="website"
                  className="pl-10"
                  value={company.website}
                  onChange={e => onInputChange('website', e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="address"
                className="pl-10"
                value={company.address}
                onChange={e => onInputChange('address', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={company.city}
                onChange={e => onInputChange('city', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={company.country}
                onChange={e => onInputChange('country', e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-center sm:justify-end">
            <Button onClick={onSave} disabled={isSaving}>
              <div className="flex items-center space-x-2">
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </LoaderOverlay>
  );
};

export default CompanyInformation;
